import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { UsersThree, Trash, Buildings, MagnifyingGlass } from '@phosphor-icons/react';

export default function AdminUsersPage() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = () => {
    api.get('/admin/users').then(r => setUsers(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updatePlan = async (userId, plan) => {
    await api.put(`/admin/users/${userId}`, { subscription_plan: plan });
    load();
  };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'disabled' ? 'active' : 'disabled';
    await api.put(`/admin/users/${userId}`, { status: newStatus });
    load();
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete ${name} and ALL their data? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${userId}`);
    load();
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const planColor = (p) => {
    if (p === 'pro') return 'bg-blue-50 text-blue-700';
    if (p === 'enterprise') return 'bg-zinc-900 text-white';
    return 'bg-zinc-100 text-zinc-600';
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>User Management</h1>
          <p className="text-sm text-zinc-500 mt-1">{users.length} registered property owners</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-sm pl-9 pr-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF] rounded-sm"
          placeholder="Search by name or email..."
          data-testid="user-search"
        />
      </div>

      {loading ? <p className="text-sm text-zinc-500">Loading...</p> : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="no-users">
          <UsersThree size={40} weight="duotone" className="text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{search ? 'No results' : 'No users yet'}</h3>
          <p className="text-sm text-zinc-500">{search ? 'Try a different search term.' : 'Users will appear here when they sign up.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200" data-testid="users-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">User</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Properties</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Plan</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Joined</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((u, i) => (
                  <tr key={u._id} className="hover:bg-zinc-50" data-testid={`user-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center text-xs font-bold text-zinc-600">
                          {u.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-bold text-zinc-900">{u.name}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-zinc-600">
                        <Buildings size={14} /> {u.property_count || 0}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.subscription_plan || 'starter'}
                        onChange={e => updatePlan(u._id, e.target.value)}
                        className={`text-xs font-bold px-2 py-1 rounded-sm border-0 cursor-pointer ${planColor(u.subscription_plan)}`}
                        data-testid={`user-plan-${i}`}
                      >
                        <option value="starter">Starter</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleStatus(u._id, u.status)}
                        className={`text-xs px-2 py-0.5 font-bold rounded-sm ${u.status === 'disabled' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}
                        data-testid={`user-status-${i}`}
                      >
                        {u.status === 'disabled' ? 'Disabled' : 'Active'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteUser(u._id, u.name)}
                        className="text-zinc-400 hover:text-red-600 transition-colors"
                        data-testid={`user-delete-${i}`}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
